import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Sparkles } from "lucide-react";
import { JsonLd } from "@/components/JsonLd";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { PublicPageLayout } from "@/components/PublicPageLayout";
import { PublicFooter } from "@/components/public-layout";
import {
	AGENT_CATEGORIES,
	CATEGORY_DESCRIPTIONS,
	CATEGORY_ICONS,
	coerceAgentCategories,
} from "@/lib/agent-categories";
import { homepageLeaderboardQueryOptions } from "@/lib/leaderboard.query";
import { breadcrumbListSchema } from "@/lib/schema";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

export const Route = createFileRoute("/categories/")({
	component: CategoriesIndexPage,
	loader: ({ context }) =>
		context.queryClient.ensureQueryData(homepageLeaderboardQueryOptions),
	head: ({ matches }) => {
		const { canonicalUrl } = getLocalizedSeoUrls({
			matches,
			internalPath: "/categories",
		});
		const title = "Browse AI Agent Categories - AgentMRR";
		const description = `Explore revenue-generating AI agents across ${AGENT_CATEGORIES.length} categories, ranked by verified MRR.`;
		return {
			meta: [
				{ title },
				{ name: "description", content: description },
				{ property: "og:title", content: title },
				{ property: "og:description", content: description },
				{ property: "og:type", content: "website" },
				{ property: "og:site_name", content: "AgentMRR" },
				{ property: "og:image", content: ogImageUrl("default") },
				{ property: "og:url", content: canonicalUrl },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl("default") },
			],
			links: buildLocalizedSeoLinks({ matches, internalPath: "/categories" }),
		};
	},
});

function CategoriesIndexPage() {
	const { data } = useQuery(homepageLeaderboardQueryOptions);
	const agents = data?.agents ?? [];

	const countByCategory = new Map<string, number>();
	for (const agent of agents) {
		for (const category of coerceAgentCategories(agent.categories)) {
			countByCategory.set(category, (countByCategory.get(category) ?? 0) + 1);
		}
	}

	const visibleCategories = AGENT_CATEGORIES.filter(
		(category) => (countByCategory.get(category.value) ?? 0) > 0,
	);

	return (
		<PublicPageLayout>
			<JsonLd
				data={breadcrumbListSchema([
					{ name: "Home", path: "/" },
					{ name: "Categories", path: "/categories" },
				])}
			/>
			<section className="relative bg-background pt-8 pb-6 sm:pt-10">
				<div className="relative mx-auto w-full max-w-5xl">
					<PageBreadcrumb className="pb-8" items={[{ label: "Categories" }]} />

					<h1
						className="font-mono text-[clamp(1.75rem,4vw,2.5rem)] leading-[1.1] tracking-[-0.02em] text-foreground"
						style={{ fontWeight: 400 }}
					>
						Browse categories
					</h1>
					<p className="mt-3 max-w-xl text-base leading-[1.55] text-muted-foreground">
						Explore verified AI agents across {visibleCategories.length}{" "}
						distinct categories, ranked by verified MRR.
					</p>
				</div>
			</section>

			<main className="mx-auto w-full max-w-5xl pt-2 pb-20">
				<div className="divide-y divide-border border-y border-border">
					{visibleCategories.map((category) => {
						const Icon = CATEGORY_ICONS[category.value] ?? Sparkles;
						const count = countByCategory.get(category.value) ?? 0;
						return (
							<Link
								key={category.value}
								to="/categories/$category"
								params={{ category: category.value }}
								className="group flex items-center gap-4 px-1 py-4 transition-colors hover:bg-brand-soft sm:px-3"
							>
								<span className="flex size-9 shrink-0 items-center justify-center text-muted-foreground transition-colors group-hover:text-brand">
									<Icon className="size-5" aria-hidden="true" />
								</span>
								<div className="min-w-0 flex-1">
									<div className="flex items-baseline gap-2">
										<span className="font-mono text-base text-foreground transition-colors group-hover:text-brand">
											{category.label}
										</span>
										<span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
											· {String(count)}
										</span>
									</div>
									<p className="mt-0.5 truncate text-sm leading-[1.5] text-muted-foreground">
										{CATEGORY_DESCRIPTIONS[category.value]}
									</p>
								</div>
								<ArrowRight
									className="size-4 shrink-0 -translate-x-1 text-muted-foreground opacity-0 transition-all group-hover:translate-x-0 group-hover:text-brand group-hover:opacity-100"
									aria-hidden="true"
								/>
							</Link>
						);
					})}
				</div>
			</main>

			<PublicFooter />
		</PublicPageLayout>
	);
}
